import { createSlice } from '@reduxjs/toolkit'
import { demoPlacedBetPlinkoGame, getMyBetsPlinko, placedBetPlinkoGame, postLightningBoardDetails } from 'redux-store/thunk/plinkoGame.thunk'
import { DEFAULT_PAGE_CALLS, DEFAULT_PLINKO_LIGHTNING_MODE_BOARD } from 'games/fast-games/PlinkoGame/plinkogame.constants'
import { BET_RESULT } from 'utils/constants/index'
import { plus, minus } from 'number-precision'

const initialState = {
  placedBetData: null,
  placedBets: [],
  betLock: false,
  betLoading: false,
  myBetsData: {
    page: 0,
    totalPages: 0,
    bets: []
  },
  wins: 0,
  losses: 0,
  profit: 0,
  wagered: 0,
  myBetsLoading: false,
  lightningBoardLoading: false,
  lightningBoardBallMultipliers: DEFAULT_PLINKO_LIGHTNING_MODE_BOARD.betMultipliers,
  lightningBoardPayouts: DEFAULT_PLINKO_LIGHTNING_MODE_BOARD.payouts,
  showWinAmt: false
}

const {
  actions: {
    setBetLock,
    setLightningBoardDetails,
    appendMyBetsPlinko,
    setPlinkoLossCount,
    setPlinkoWinsCount,
    setPlinkoProfit,
    setPlinkoWagered,
    setShowWinAmt
  },
  reducer
} = createSlice({
  name: 'plinkoGame',
  initialState,
  reducers: {
    setPlinkoWinsCount: (state, action) => {
      if (action.payload) {
        return {
          ...state,
          wins: action.payload.reset
        }
      }
      return {
        ...state,
        wins: state.wins + 1
      }
    },
    setPlinkoLossCount: (state, action) => {
      if (action.payload) {
        return {
          ...state,
          losses: action.payload.reset
        }
      }
      return {
        ...state,
        losses: state.losses + 1
      }
    },
    setPlinkoProfit: (state, action) => {
      return {
        ...state,
        profit: action.payload
      }
    },
    setPlinkoWagered: (state, action) => {
      return {
        ...state,
        wagered: action.payload
      }
    },
    setBetLock: (state, action) => {
      return {
        ...state,
        betLock: !(state?.placedBets?.length === 0)
      }
    },
    setLightningBoardDetails: (state, action) => {
      return {
        ...state,
        lightningBoardBallMultipliers: action.payload?.lightningBoardBallMultipliers ?? DEFAULT_PLINKO_LIGHTNING_MODE_BOARD.betMultipliers,
        lightningBoardPayouts: action.payload?.lightningBoardPayouts ?? DEFAULT_PLINKO_LIGHTNING_MODE_BOARD.payouts
      }
    },
    appendMyBetsPlinko: (state, action) => {
      let _placedBets = [...(state?.placedBets ?? [])]
      const _betToAppend = _placedBets?.[_placedBets.length - 1]
      _placedBets = _placedBets?.slice(0, _placedBets.length - 1)
      const { nextServerSeedHash, ...data } = _betToAppend || { nextServerSeedHash: '', data: {} }
      if (nextServerSeedHash) {
        const myBetsDataRows = [data, ...(state?.myBetsData?.bets ?? [])]
        if (myBetsDataRows.length > DEFAULT_PAGE_CALLS) {
          myBetsDataRows.pop()
        }
        const profit = plus(state.profit, minus(_betToAppend?.winningAmount ?? 0, _betToAppend?.betAmount ?? 0))
        const wagered = plus(state.wagered, _betToAppend?.betAmount)
        const wins = plus(state.wins, _betToAppend?.result === BET_RESULT.WON ? 1 : 0)
        const losses = plus(state.losses, _betToAppend?.result === BET_RESULT.LOST ? 1 : 0)
        return {
          ...state,
          placedBets: _placedBets,
          ...(_placedBets?.length === 0
            ? {
                betLock: false
              }
            : {}),
          profit,
          wagered,
          wins,
          losses,
          myBetsData: {
            page: state.myBetsData?.page,
            totalPages: state.myBetsData?.totalPages,
            bets: myBetsDataRows.slice(0, 10)
          }
        }
      }
      return state
    },
    setShowWinAmt: (state, action) => {
      return {
        ...state,
        showWinAmt: action.payload
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(placedBetPlinkoGame.pending, (state, action) => {
        return {
          ...state,
          betLock: true,
          betLoading: true
        }
      })
      .addCase(placedBetPlinkoGame.fulfilled, (state, action) => {
        const newBet = {
          ...action.payload.bet,
          nextServerSeedHash: action.payload.nextServerSeedHash
        }

        return {
          ...state,
          placedBets: [newBet, ...state.placedBets],
          placedBetData: newBet,
          betLoading: false
        }
      })
      .addCase(placedBetPlinkoGame.rejected, (state, action) => {
        return {
          ...state,
          betLock: false,
          betLoading: false
        }
      })
      .addCase(demoPlacedBetPlinkoGame.pending, (state, action) => {
        return {
          ...state,
          betLock: true,
          betLoading: true
        }
      })
      .addCase(demoPlacedBetPlinkoGame.fulfilled, (state, action) => {
        return {
          ...state,
          placedBets: [action.payload, ...(state?.placedBets ?? [])],
          placedBetData: action.payload,
          betLoading: false
        }
      })
      .addCase(demoPlacedBetPlinkoGame.rejected, (state, action) => {
        return {
          ...state,
          betLock: false,
          betLoading: false
        }
      })
      .addCase(getMyBetsPlinko.pending, (state, action) => {
        return {
          ...state,
          myBetsLoading: true

        }
      })
      .addCase(getMyBetsPlinko.fulfilled, (state, action) => {
        return {
          ...state,
          myBetsData: action.payload,
          myBetsLoading: false

        }
      })
      .addCase(postLightningBoardDetails.pending, (state, action) => {
        return {
          ...state,
          lightningBoardLoading: true

        }
      })
      .addCase(postLightningBoardDetails.fulfilled, (state, action) => {
        const lightningBoardDetails = action.payload?.lightningBoardDetails
        return {
          ...state,
          lightningBoardBallMultipliers: lightningBoardDetails?.betMultipliers,
          lightningBoardPayouts: lightningBoardDetails?.payouts,
          lightningBoardLoading: false
        }
      })
      .addCase(postLightningBoardDetails.rejected, (state, action) => {
        return {
          ...state,
          lightningBoardLoading: false
        }
      })
  }
})

export {
  setBetLock,
  setLightningBoardDetails,
  appendMyBetsPlinko,
  setPlinkoLossCount,
  setPlinkoWinsCount,
  setPlinkoProfit,
  setPlinkoWagered,
  setShowWinAmt
}

export default reducer
